import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { LANGUAGE_NATIVE_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../lib/i18n/config';

interface FooterProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

export function Footer({ currentLanguage, onLanguageChange }: FooterProps) {
  const { t } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const languageFlags: Record<SupportedLanguage, string> = {
    en: '🇺🇸',
    ja: '🇯🇵',
    'zh-Hans': '🇨🇳',
    ko: '🇰🇷',
    es: '🇪🇸',
  };

  const languages = SUPPORTED_LANGUAGES.map((code) => ({
    code,
    name: LANGUAGE_NATIVE_NAMES[code],
    flag: languageFlags[code],
  }));

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const quickLinks = [
    { to: '/date-difference', label: t('nav.dateDifference') },
    { to: '/add-subtract', label: t('nav.addSubtract') },
    { to: '/business-days', label: t('nav.businessDays') },
    { to: '/weekday', label: t('nav.weekday') },
    { to: '/week-number', label: t('nav.weekNumber') },
    { to: '/other-tools', label: t('nav.otherToolsPage') },
  ];

  return (
    <footer className="border-t border-blue-100/80 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr_0.8fr_0.9fr]">
          <div>
            <Link to="/" className="mb-4 inline-flex items-center gap-3 rounded-xl">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white" aria-hidden="true">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <span className="text-2xl font-black tracking-tight text-slate-950">DateCalculator</span>
            </Link>
            <p className="max-w-sm text-sm leading-6 text-slate-600">{t('footer.description')}</p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold text-slate-700">
              <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-2">{t('footer.madeFor')}</span>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-black uppercase text-blue-600">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm font-medium text-slate-600 transition hover:text-blue-700">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-black uppercase text-blue-600">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              {[
                { to: '/privacy', label: t('footer.privacyPolicy') },
                { to: '/terms', label: t('footer.terms') },
                { to: '/contact', label: t('nav.contact') },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm font-medium text-slate-600 transition hover:text-blue-700">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-black uppercase text-blue-600">{t('common.language')}</h3>
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex w-full items-center gap-2 rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-blue-50"
                aria-label={t('common.changeLanguage')}
              >
                <span className="text-lg">{currentLang.flag}</span>
                <span>{currentLang.name}</span>
                <svg className={`ml-auto h-4 w-4 transition ${isLangMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLangMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 z-50 mb-2 rounded-2xl border border-blue-100 bg-white p-2 shadow-[0_18px_50px_rgba(15,23,42,0.14)]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        onLanguageChange(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition ${
                        currentLanguage === lang.code ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-blue-100 pt-6 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <div className="flex gap-6">
            <a href="/sitemap.xml" className="transition hover:text-blue-700">
              {t('footer.sitemap')}
            </a>
            <Link to="/about" className="transition hover:text-blue-700">
              {t('nav.about')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
